import React, { useState, FormEvent, ReactNode } from 'react';
import styles from './ResetPassword.module.scss';
import { useQueryStringParameters } from 'lib/hooks/browser/useQueryString';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { PasswordInputField } from 'components/helpers/Atoms/FormElements/PasswordInputField';
import { NewPasswordWithCriteria } from 'components/helpers/Atoms/FormElements/NewPasswordWithCriteria';
import clsx from 'clsx';
import useDictionary from 'lib/hooks/useDictionary';
import { useFormErrors } from 'lib/hooks/useFormErrors';
import { getPasswordRuleStatus, passwordRules } from 'lib/utils/validation/password-validation';

const FORM_FIELD_IDS = {
  NEW_PASSWORD: 'newPassword',
  CONFIRM_PASSWORD: 'confirmPassword',
} as const;

export interface ResetPasswordProps {
  title: ReactNode;
  newPasswordLabel: ReactNode;
  confirmPasswordLabel: ReactNode;
  submitButtonText: ReactNode;
  successMessage: ReactNode;
  loginLink: ReactNode;
  missingParamsMessage: ReactNode;
  requestNewPasswordLink: ReactNode;
  isEditing: boolean;
}

export default function ResetPassword(props: ResetPasswordProps) {
  const {
    title,
    newPasswordLabel,
    confirmPasswordLabel,
    submitButtonText,
    successMessage,
    loginLink,
    missingParamsMessage,
    requestNewPasswordLink,
    isEditing,
  } = props;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isInvalidToken, setIsInvalidToken] = useState(false);

  // Track password criteria validation state
  const [isNewPasswordMeetingCriteria, setIsNewPasswordMeetingCriteria] = useState<
    boolean | undefined
  >(undefined);

  // Get query parameters from URL
  const [codeParam, emailParam] = useQueryStringParameters('c', 'email');
  const hasRequiredParams = !!(codeParam && emailParam) && !isInvalidToken;

  const { getDictionaryValue } = useDictionary();

  const { setFieldError, clearAllErrors, getFieldError } = useFormErrors();

  const getNewPasswordCriteriaError = (passwordValue: string): string | null => {
    const trimmed = passwordValue.trim();
    if (trimmed.length === 0) return null;

    const ruleStatus = getPasswordRuleStatus(trimmed, passwordRules);
    const failedRules = passwordRules.filter((rule) => !ruleStatus[rule.id]);
    if (failedRules.length === 0) return null;

    const siteName = process.env.SITECORE_SITE_NAME;

    // NAF (GMFS) requires a specific inline error message.
    if (siteName === 'GMFS') {
      return getDictionaryValue(
        'Components.MyAccount.ErrorMessages.PasswordInlineError',
        'Password must be at least 8 characters and contain at least 3 of the following types: uppercase letter, lowercase letter, number and symbol.'
      );
    }

    const ruleLabels = failedRules.map((rule) =>
      rule.dictionaryKey ? getDictionaryValue(rule.dictionaryKey, rule.label) : rule.label
    );

    const passwordMust = getDictionaryValue('Components.MyAccount.PasswordMust', 'Password must');

    if (ruleLabels.length === 1) {
      return `${passwordMust} ${ruleLabels[0]}.`;
    }

    if (ruleLabels.length === 2) {
      return `${passwordMust} ${ruleLabels[0]} and ${ruleLabels[1]}.`;
    }

    const labels = [...ruleLabels];
    const lastLabel = labels.pop();
    return `${passwordMust} ${labels.join(', ')}, and ${lastLabel}.`;
  };

  const focusField = (fieldId: string) => {
    const el = document.getElementById(fieldId) as HTMLInputElement | null;
    if (!el) return;

    // Inline errors + aria-describedby so screen readers announce the error message,
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        el.focus();
      });
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitSuccess(false);
    setSubmitError('');
    setHasAttemptedSubmit(true);

    // Validate only custom business logic (components handle their own validation)
    clearAllErrors();
    const aggregatedErrors: string[] = [];
    let hasValidationErrors = false;

    // TODO: Reorganize these dictionaries to common folder later
    // Get mismatch error message (same one used by PasswordInputField component)
    const mismatchErrorMessage = getDictionaryValue(
      'Components.MyAccount.ErrorMessages.PasswordsDoNotMatch',
      'Passwords do not match.'
    );

    const emptyFieldsErrorMessage = getDictionaryValue(
      'Components.ForgotPassword.EmptyFieldsSubmission',
      'There was an error resetting your password.| Please try again.'
    );

    const criteriaErrorMessage = getNewPasswordCriteriaError(newPassword);
    const hasCriteriaError = Boolean(criteriaErrorMessage);

    // Validate new password - required and criteria validation
    if (!newPassword.trim()) {
      const requiredMessage = getDictionaryValue(
        'Components.MyAccount.ErrorMessages.NewPasswordRequired',
        'Please enter your new password.'
      );
      setFieldError(FORM_FIELD_IDS.NEW_PASSWORD, requiredMessage);
      aggregatedErrors.push(requiredMessage);
      hasValidationErrors = true;
    } else if (hasCriteriaError) {
      if (criteriaErrorMessage) {
        aggregatedErrors.push(criteriaErrorMessage);
      }
      hasValidationErrors = true;
    }

    // Validate confirm password - required
    if (!confirmPassword.trim()) {
      const requiredMessage = getDictionaryValue(
        'Components.MyAccount.ErrorMessages.ConfirmPasswordRequired',
        'Please confirm your new password.'
      );
      setFieldError(FORM_FIELD_IDS.CONFIRM_PASSWORD, requiredMessage);
      aggregatedErrors.push(requiredMessage);
      hasValidationErrors = true;
    } else if (newPassword !== confirmPassword) {
      // Use the same error message as PasswordInputField component's mismatchErrorMessage
      // This ensures consistency and avoids duplication
      setFieldError(FORM_FIELD_IDS.CONFIRM_PASSWORD, mismatchErrorMessage);
      aggregatedErrors.push(mismatchErrorMessage);
      hasValidationErrors = true;
    }

    // If validation errors, stop here
    if (hasValidationErrors) {
      const summarizedErrors = Array.from(new Set(aggregatedErrors)).join(' ');
      setSubmitError(
        emptyFieldsErrorMessage
          ? `${emptyFieldsErrorMessage} ${summarizedErrors}`.trim()
          : summarizedErrors
      );

      // Ensure criteria error is shown inline after submit.
      if (hasCriteriaError) {
        setIsNewPasswordMeetingCriteria(false);
      }

      // Move focus to the first field with an error so screen readers announce the inline error.
      const shouldFocusNewPassword =
        !newPassword.trim() || hasCriteriaError || !!getFieldError(FORM_FIELD_IDS.NEW_PASSWORD);
      if (shouldFocusNewPassword) {
        focusField(FORM_FIELD_IDS.NEW_PASSWORD);
      } else {
        focusField(FORM_FIELD_IDS.CONFIRM_PASSWORD);
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError('');

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailParam,
          passwordResetToken: codeParam,
          newPassword: newPassword.trim(),
        }),
      });

      if (!response.ok) {
        // Switch to invalid token view for any submission failure
        const errorMsg = getDictionaryValue(
          'Components.ForgotPassword.PasswordResetFailed',
          'There was an error resetting your password.| Please try again.'
        );

        const data = await response.json();
        if (response.status === 400 && data.error === 'INVALID_TOKEN') {
          setIsInvalidToken(true);
          setSubmitError(errorMsg); // Generic Error Message for now, token expiration specific message can be added here if needed
        } else {
          setSubmitError(errorMsg);
        }
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);

      setSubmitSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      setHasAttemptedSubmit(false);
      clearAllErrors();
      setIsNewPasswordMeetingCriteria(undefined);
    } catch (error) {
      console.error('Reset password error:', error);
      // Switch to invalid token view for any submission failure
      setIsInvalidToken(true);
      setIsSubmitting(false);
      // Show generic error message for any failure
      const errorMsg = getDictionaryValue(
        'Components.ForgotPassword.PasswordResetFailed',
        'There was an error resetting your password.| Please try again.'
      );
      setSubmitError(errorMsg);
    }
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  // Show missing params view if required params are not present
  if (!hasRequiredParams && !isEditing) {
    return (
      <div className={styles.resetPasswordContainer}>
        <div className={styles.resetPassword}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <h1 className={styles.title}>{title}</h1>
            </div>
            <hr className={styles.divider} />
          </div>

          <div className={styles.content}>
            <div className={styles.missingParamsView}>
              {/* Show error message if invalid token (user was redirected here after submit) */}
              {isInvalidToken && submitError && (
                <div className={styles.errorBannerWrapper}>
                  <MessageBanner type="error" message={submitError} autoFocus />
                </div>
              )}
              <div className={styles.missingParamsMessage}>{missingParamsMessage}</div>
              <div className={styles.requestNewPasswordButtonWrapper}>{requestNewPasswordLink}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.resetPasswordContainer}>
      <div className={styles.resetPassword}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>{title}</h1>
          </div>
          <hr className={styles.divider} />
        </div>

        <div className={styles.content}>
          {submitSuccess ? (
            <div className={styles.successView}>
              {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
              {loginLink && <div className={styles.loginLink}>{loginLink}</div>}
            </div>
          ) : (
            <>
              {submitError && (
                <div className={styles.errorBannerWrapper}>
                  <MessageBanner type="error" message={submitError} />
                </div>
              )}

              <div className={styles.requiredIndicatorWrapper}>
                {/* //TODO: Reorganize these dictionaries to common folder later */}
                {getDictionaryValue('Components.MyAccount.Required', '* Required')}
              </div>

              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.inputs}>
                  <div className={clsx(styles.formFields)}>
                    <NewPasswordWithCriteria
                      id={FORM_FIELD_IDS.NEW_PASSWORD}
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                      label={newPasswordLabel}
                      labelFor={FORM_FIELD_IDS.NEW_PASSWORD}
                      // TODO: Reorganize these dictionaries to common folder later
                      placeholder={getDictionaryValue(
                        'Components.UpdatePassword.MaskedPassword',
                        '••••••••••••'
                      )}
                      required
                      validateOnChange={true}
                      validateOnBlur={false}
                      showLeftIcon={hasAttemptedSubmit}
                      error={
                        hasAttemptedSubmit
                          ? (getFieldError('newPassword') as string | undefined)
                          : undefined
                      }
                      showInlineError={
                        hasAttemptedSubmit &&
                        (!!getFieldError('newPassword') || isNewPasswordMeetingCriteria === false)
                      }
                      onValidationChange={(isValid) => {
                        setIsNewPasswordMeetingCriteria(isValid);
                      }}
                      disabled={isSubmitting}
                    />

                    <PasswordInputField
                      id={FORM_FIELD_IDS.CONFIRM_PASSWORD}
                      label={confirmPasswordLabel}
                      labelFor={FORM_FIELD_IDS.CONFIRM_PASSWORD}
                      value={confirmPassword}
                      // TODO: Reorganize these dictionaries to common folder later
                      placeholder={getDictionaryValue(
                        'Components.UpdatePassword.MaskedPassword',
                        '••••••••••••'
                      )}
                      onChange={handleConfirmPasswordChange}
                      valueToMatch={newPassword}
                      // TODO: Reorganize these dictionaries to common folder later
                      mismatchErrorMessage={getDictionaryValue(
                        'Components.MyAccount.ErrorMessages.PasswordsDoNotMatch',
                        'Passwords do not match.'
                      )}
                      error={
                        hasAttemptedSubmit
                          ? (getFieldError('confirmPassword') as string | undefined)
                          : undefined
                      }
                      showInlineError={hasAttemptedSubmit && !!getFieldError('confirmPassword')}
                      validateOnChange={false}
                      validateOnBlur={false}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className={styles.actions}>
                  <CtaButton
                    ctaStyle="filled"
                    ctaVariant="brand"
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {submitButtonText}
                  </CtaButton>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
